import { Box, Group, Stack } from '@mantine/core';
import type { IAMRoleOut } from '@repo/api-codegen';
import { useApiListIamRoles } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Button, Text, Title } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspacePermissionV2 } from '../../../api/hooks/workspace/useWorkspacePermissionV2';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { IamRoleMembersListButton } from '../../IamRoleMembersListButton/IamRoleMembersListButton';
import { TableV2 } from '../../TableV2';
import { ROW_HEIGHT } from '../../TableV2/TableV2.styles';
import type { ExtendedDataTableColumn } from '../../TableV2/types';
import { UserAvatar } from '../../UserAvatar';
import type { AvatarUser } from '../../UserAvatar/common';
import { IamRoleActionCell } from './IAMRole/IamRoleActionCell';

const secodaAvatarUser = {
	id: 'secoda',
	display_name: 'Secoda',
	first_name: '',
	last_name: '',
	email: 'support@secoda.co',
	profile_picture: '/images/logos/cfsecoda.webp',
} as AvatarUser;

export function IamRolesTab() {
	const navigate = useNavigate();
	const { hasPermission: canCreateRole } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Roles.Create',
		defaultResult: false,
	});

	const migratedPermissionV2 = useWorkspacePermissionV2();

	const columns = useMemo<ExtendedDataTableColumn<IAMRoleOut>[]>(
		() => [
			{
				titleLabel: 'Role',
				accessor: 'name',
				render: (iamRole) => (
					<Stack maw={340} mah={`${ROW_HEIGHT}px`} spacing={0}>
						<Title size="sm">{iamRole.name}</Title>
						<Text size="xs" color="text/secondary/default" lineClamp={1}>
							{iamRole.description}
						</Text>
					</Stack>
				),
			},
			{
				titleLabel: 'Created by',
				accessor: 'created_by',
				render: (iamRole) => (
					<Group mah={`${ROW_HEIGHT}px`} spacing="xs" noWrap>
						{iamRole.is_legacy || !iamRole.created_by ? (
							<Group spacing="xs" noWrap>
								<UserAvatar user={secodaAvatarUser} size="xs" />
								<Text size="sm">Secoda</Text>
							</Group>
						) : (
							<Group spacing="xs" noWrap>
								{iamRole.created_by && (
									<UserAvatar user={iamRole.created_by} size="xs" />
								)}
								<Text size="sm">{iamRole.created_by?.display_name}</Text>
							</Group>
						)}
					</Group>
				),
			},
			{
				titleLabel: 'Members',
				accessor: 'members',
				render: (iamRole) => <IamRoleMembersListButton iamRole={iamRole} />,
			},
			{
				accessor: 'actions',
				titleLabel: '',
				textAlignment: 'right',
				render: (iamRole) => <IamRoleActionCell iamRole={iamRole} />,
			},
		],
		[]
	);

	const editRow = useCallback(
		(iamRole: IAMRoleOut) => {
			navigate(`/roles/edit/${iamRole.id}`);
		},
		[navigate]
	);

	return (
		<Box py="md">
			<TableV2<IAMRoleOut>
				withAdditionalButtons={
					canCreateRole && migratedPermissionV2 ? (
						<Button variant="primary" onClick={() => navigate('/roles/new')}>
							Create role
						</Button>
					) : undefined
				}
				columns={columns}
				withSearch
				onRowClick={editRow}
				pluralTypeString="roles"
				withStickyColumnBorder={false}
				// @ts-ignore
				usePaginationList={useApiListIamRoles}
				useCodegenListInterface={true}
				withInteractiveHeader={false}
			/>
		</Box>
	);
}
